import React, { useMemo, useState } from "react";
import Pallet3D from "@/components/Pallet3D";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowsClockwise, ArrowsLeftRight, Copy, FloppyDisk,
  LockKey, LockOpen, PencilSimple, Plus, Trash, X,
} from "@phosphor-icons/react";

const STEP = 5;
const EPS = 1e-6;
const clone = (value) => JSON.parse(JSON.stringify(value));
const SUPPORT_OPTIONS = [40, 50, 60, 70, 80, 90, 100];

const supportHelp = (percent) => {
  if (percent <= 50) return {
    tone: "border-red-300 bg-red-50 text-red-900",
    title: "Düşük destek — dikkat",
    text: `Üstteki kutunun tabanının yalnızca en az %${percent}'ının alttaki kutulara temas etmesi yeterli kabul edilir. Kutunun büyük bir kısmı boşta kalabilir. Gerçek yüklemede kayma/devrilme riski artabileceği için fiziksel sabitleme ve yetkili kontrolü gerekir.`,
  };
  if (percent <= 60) return {
    tone: "border-amber-300 bg-amber-50 text-amber-900",
    title: "Esnek destek",
    text: `Üstteki kutunun tabanının en az %${percent}'ı alttaki kutulara temas etmelidir. Daha fazla yerleşime izin verir ancak boşta kalan alan artar; gerçek yüklemede dengeyi ayrıca kontrol edin.`,
  };
  if (percent <= 70) return {
    tone: "border-blue-200 bg-blue-50 text-blue-900",
    title: "Önerilen varsayılan",
    text: `Üstteki kutunun tabanının en az %${percent}'ı alttaki kutulara temas etmelidir. Palet verimliliği ile fiziksel destek arasında dengeli varsayılan seçenektir.`,
  };
  return {
    tone: "border-emerald-200 bg-emerald-50 text-emerald-900",
    title: "Yüksek destek",
    text: `Üstteki kutunun tabanının en az %${percent}'ı alttaki kutulara temas etmelidir. Daha sıkı ve daha stabil yerleşim ister; palet doluluğu azalabilir.`,
  };
};

const axisLimit = (pallet, axis, fallback) => {
  const key = axis === "x" ? "L" : axis === "y" ? "W" : "H";
  const enabledKey = axis === "x" ? "tolerance_x" : axis === "y" ? "tolerance_y" : "tolerance_z";
  const base = Number(pallet?.[key] || fallback || 0);
  const tolerance = Number(pallet?.tolerance || 0);
  const enabled = axis === "z" ? pallet?.[enabledKey] !== false : !!pallet?.[enabledKey];
  return base + (enabled ? tolerance : 0);
};

const overlapsExisting = (pallet, x, y, z, dx, dy, dz, skipIdx = -1) => (pallet?.boxes || []).some((b, i) =>
  i !== skipIdx &&
  x < Number(b.x) + Number(b.dx) - EPS && x + dx > Number(b.x) + EPS &&
  y < Number(b.y) + Number(b.dy) - EPS && y + dy > Number(b.y) + EPS &&
  z < Number(b.z) + Number(b.dz) - EPS && z + dz > Number(b.z) + EPS
);

const fits = (pallet, x, y, z, dx, dy, dz) => {
  const L = axisLimit(pallet, "x", pallet?.L);
  const W = axisLimit(pallet, "y", pallet?.W);
  const H = axisLimit(pallet, "z", pallet?.H);
  return x >= -EPS && y >= -EPS && z >= -EPS && x + dx <= L + EPS && y + dy <= W + EPS && z + dz <= H + EPS;
};

const findGravityZBelow = (pallet, x, y, currentZ, dx, dy, skipIdx = -1) => {
  let restingZ = 0;
  (pallet?.boxes || []).forEach((b, i) => {
    if (i === skipIdx || b.can_support_load === false) return;
    const ox = Math.min(x + dx, Number(b.x) + Number(b.dx)) - Math.max(x, Number(b.x));
    const oy = Math.min(y + dy, Number(b.y) + Number(b.dy)) - Math.max(y, Number(b.y));
    if (ox <= EPS || oy <= EPS) return;
    const top = Number(b.z) + Number(b.dz);
    if (top <= currentZ + EPS) restingZ = Math.max(restingZ, top);
  });
  return Number(restingZ.toFixed(2));
};

const findRestingZ = (pallet, x, y, dx, dy, dz, skipIdx = -1) => {
  const levels = new Set([0]);
  (pallet?.boxes || []).forEach((b, i) => {
    if (i === skipIdx || b.can_support_load === false) return;
    const ox = Math.min(x + dx, Number(b.x) + Number(b.dx)) - Math.max(x, Number(b.x));
    const oy = Math.min(y + dy, Number(b.y) + Number(b.dy)) - Math.max(y, Number(b.y));
    if (ox > EPS && oy > EPS) levels.add(Number((Number(b.z) + Number(b.dz)).toFixed(2)));
  });
  for (const z of [...levels].sort((a, b) => a - b)) {
    if (fits(pallet, x, y, z, dx, dy, dz) && !overlapsExisting(pallet, x, y, z, dx, dy, dz, skipIdx)) return z;
  }
  return null;
};

const findFreePosition = (pallet, dx, dy, dz, skipIdx = -1) => {
  const maxX = axisLimit(pallet, "x", pallet?.L);
  const maxY = axisLimit(pallet, "y", pallet?.W);
  const anchors = new Set(["0|0"]);
  (pallet?.boxes || []).forEach((b, i) => {
    if (i === skipIdx) return;
    anchors.add(`${Number(b.x) + Number(b.dx)}|${Number(b.y)}`);
    anchors.add(`${Number(b.x)}|${Number(b.y) + Number(b.dy)}`);
    anchors.add(`${Number(b.x) + Number(b.dx)}|${Number(b.y) + Number(b.dy)}`);
    anchors.add(`${Number(b.x)}|${Number(b.y)}`);
  });
  const candidates = [...anchors].map((key) => {
    const [x, y] = key.split("|").map(Number);
    return { x, y };
  }).filter(({ x, y }) => x >= -EPS && y >= -EPS && x + dx <= maxX + EPS && y + dy <= maxY + EPS)
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (const { x, y } of candidates) {
    const z = findRestingZ(pallet, x, y, dx, dy, dz, skipIdx);
    if (z != null) return { x: Number(x.toFixed(2)), y: Number(y.toFixed(2)), z };
  }
  return null;
};

const settleUnsupported = (pallet, pinnedIdx = -1) => {
  const next = pallet;
  for (let pass = 0; pass < Math.max(1, next.boxes.length + 1); pass += 1) {
    let changed = false;
    const order = next.boxes.map((b, i) => ({ i, z: Number(b.z) })).sort((a, b) => a.z - b.z || a.i - b.i);
    for (const { i } of order) {
      if (i === pinnedIdx) continue;
      const b = next.boxes[i];
      if (!b || b.locked || Number(b.z) <= EPS) continue;
      const targetZ = findGravityZBelow(next, Number(b.x), Number(b.y), Number(b.z), Number(b.dx), Number(b.dy), i);
      if (targetZ >= Number(b.z) - EPS || overlapsExisting(next, Number(b.x), Number(b.y), targetZ, Number(b.dx), Number(b.dy), Number(b.dz), i)) continue;
      next.boxes[i] = { ...b, z: targetZ };
      changed = true;
    }
    if (!changed) break;
  }
  return next;
};

const recompute = (pallet) => {
  const p = { ...pallet, boxes: pallet?.boxes || [] };
  const used = p.boxes.reduce((sum, b) => sum + Number(b.dx) * Number(b.dy) * Number(b.dz), 0);
  const totalWeight = p.boxes.reduce((sum, b) => sum + Number(b.net_kg || 0), 0);
  const top = p.boxes.length ? Math.max(...p.boxes.map((b) => Number(b.z) + Number(b.dz))) : 0;
  const volume = Number(p.L || 0) * Number(p.W || 0) * Number(p.H || 0);
  return {
    ...p,
    box_count: p.boxes.length,
    total_weight: Number(totalWeight.toFixed(3)),
    top_height: Number(top.toFixed(2)),
    fill_ratio: volume ? Number((used / volume).toFixed(4)) : Number(p.fill_ratio || 0),
  };
};

const ROTATIONS = [
  { key: "0", label: "D×G×Y", fn: (l, w, h) => [l, w, h] },
  { key: "1", label: "D×Y×G", fn: (l, w, h) => [l, h, w] },
  { key: "2", label: "G×D×Y", fn: (l, w, h) => [w, l, h] },
  { key: "3", label: "G×Y×D", fn: (l, w, h) => [w, h, l] },
  { key: "4", label: "Y×D×G", fn: (l, w, h) => [h, l, w] },
  { key: "5", label: "Y×G×D", fn: (l, w, h) => [h, w, l] },
];

export default function NestedPalletEditor({
  planId, loads = [], products = {}, unitLabel = "Yükleme Alanı",
  readOnly = false, lockToken = "", onUpdated,
}) {
  const [editing, setEditing] = useState(null);
  const [draftPallet, setDraftPallet] = useState(null);
  const [selectedBoxIdx, setSelectedBoxIdx] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addSku, setAddSku] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [addRotation, setAddRotation] = useState("auto");
  const [moveTarget, setMoveTarget] = useState("");
  const [supportPercent, setSupportPercent] = useState(70);

  const selected = useMemo(() => loads.find((row) => row.palletNo === editing) || null, [loads, editing]);
  const productOptions = useMemo(() => Object.values(products || {}).filter((p) => p?.sku).sort((a, b) => a.sku.localeCompare(b.sku, "tr")), [products]);
  const visualPallet = draftPallet ? recompute(draftPallet) : null;
  const selectedBox = visualPallet?.boxes?.[selectedBoxIdx] || null;
  const supportInfo = supportHelp(Number(supportPercent));

  const updateDraft = (mutator, pinIdx = -1) => {
    setDraftPallet((prev) => {
      if (!prev) return prev;
      const next = clone(prev);
      mutator(next);
      settleUnsupported(next, pinIdx);
      return recompute(next);
    });
    setDirty(true);
  };

  const openEditor = (palletNo) => {
    const row = loads.find((item) => item.palletNo === palletNo);
    if (!row?.sourcePallet) return toast.error("Palet geometrisi bulunamadı.");
    const source = clone(row.sourcePallet);
    if (!source.L) source.L = 120;
    if (!source.W) source.W = 100;
    if (!source.H) source.H = Math.max(Number(row.totalHeight || 0), 200);
    const savedSupport = Number(source.manual_min_support);
    setSupportPercent(Number.isFinite(savedSupport) && savedSupport >= 0.4 && savedSupport <= 1 ? Math.round(savedSupport * 100) : 70);
    setEditing(palletNo);
    setDraftPallet(recompute(source));
    setSelectedBoxIdx(null);
    setDirty(false);
    setAddSku("");
    setAddQty(1);
    setAddRotation("auto");
    setMoveTarget("");
  };

  const closeEditor = () => {
    if (dirty && !window.confirm("Kaydedilmemiş manuel palet değişiklikleri var. Kapatılsın mı?")) return;
    setEditing(null);
    setDraftPallet(null);
    setSelectedBoxIdx(null);
    setDirty(false);
  };

  const rotateSelected = (axis) => {
    if (selectedBoxIdx == null || !selectedBox || selectedBox.locked) return;
    const original = clone(selectedBox);
    let dx = Number(original.dx), dy = Number(original.dy), dz = Number(original.dz);
    if (axis === "z") [dx, dy] = [dy, dx];
    else if (axis === "x") [dy, dz] = [dz, dy];
    else if (axis === "y") [dx, dz] = [dz, dx];
    const product = products[original.sku];
    const allowed = product?.placement_rules?.allowed_rotations || [0, 1, 2, 3, 4, 5];
    const orientationOk = ROTATIONS.some((r) => {
      if (!allowed.includes(Number(r.key))) return false;
      const [a, b, c] = r.fn(Number(product?.length || 0), Number(product?.width || 0), Number(product?.height || 0));
      return Math.abs(a - dx) <= 0.05 && Math.abs(b - dy) <= 0.05 && Math.abs(c - dz) <= 0.05;
    });
    if (product && !orientationOk) return toast.error("Bu ürün için bu rotasyona izin verilmiyor.");

    const trial = clone(visualPallet);
    const maxX = axisLimit(trial, "x", trial.L) - dx;
    const maxY = axisLimit(trial, "y", trial.W) - dy;
    const cx = Number(original.x) + Number(original.dx) / 2;
    const cy = Number(original.y) + Number(original.dy) / 2;
    let x = Math.max(0, Math.min(cx - dx / 2, maxX));
    let y = Math.max(0, Math.min(cy - dy / 2, maxY));
    let z = Number(original.z);
    if (!fits(trial, x, y, z, dx, dy, dz) || overlapsExisting(trial, x, y, z, dx, dy, dz, selectedBoxIdx)) {
      const spot = findFreePosition(trial, dx, dy, dz, selectedBoxIdx);
      if (!spot) return toast.error("Bu rotasyon için çakışmasız alan bulunamadı.");
      ({ x, y, z } = spot);
    }
    updateDraft((p) => {
      p.boxes[selectedBoxIdx] = { ...p.boxes[selectedBoxIdx], x: Number(x.toFixed(2)), y: Number(y.toFixed(2)), z: Number(z.toFixed(2)), dx, dy, dz };
    }, selectedBoxIdx);
  };

  const nudgeSelected = (axis, delta) => {
    if (selectedBoxIdx == null || !selectedBox || selectedBox.locked) return;
    const p = visualPallet;
    const b = selectedBox;
    let x = Number(b.x), y = Number(b.y), z = Number(b.z);
    if (axis === "x") x = Math.max(0, Math.min(x + delta, axisLimit(p, "x", p.L) - Number(b.dx)));
    if (axis === "y") y = Math.max(0, Math.min(y + delta, axisLimit(p, "y", p.W) - Number(b.dy)));
    if (axis === "z") {
      if (delta < 0) z = findGravityZBelow(p, x, y, z, Number(b.dx), Number(b.dy), selectedBoxIdx);
      else z = Math.max(0, Math.min(z + delta, axisLimit(p, "z", p.H) - Number(b.dz)));
    } else {
      const resting = findRestingZ(p, x, y, Number(b.dx), Number(b.dy), Number(b.dz), selectedBoxIdx);
      if (resting == null) return toast.error("Bu yönde hareket kutuyla çakışıyor.");
      z = resting;
    }
    if (!fits(p, x, y, z, Number(b.dx), Number(b.dy), Number(b.dz)) || overlapsExisting(p, x, y, z, Number(b.dx), Number(b.dy), Number(b.dz), selectedBoxIdx)) {
      return toast.error("Bu hareket palet sınırını veya başka bir kutuyu ihlal ediyor.");
    }
    updateDraft((next) => { next.boxes[selectedBoxIdx] = { ...next.boxes[selectedBoxIdx], x: Number(x.toFixed(2)), y: Number(y.toFixed(2)), z: Number(z.toFixed(2)) }; }, selectedBoxIdx);
  };

  const onDropBox = ({ preview, idx, x, y }) => {
    if (!preview || readOnly) return;
    const b = visualPallet?.boxes?.[idx];
    if (!b || b.locked) return;
    const maxX = axisLimit(visualPallet, "x", visualPallet.L) - Number(b.dx);
    const maxY = axisLimit(visualPallet, "y", visualPallet.W) - Number(b.dy);
    const nx = Math.max(0, Math.min(Number(x), maxX));
    const ny = Math.max(0, Math.min(Number(y), maxY));
    const nz = findRestingZ(visualPallet, nx, ny, Number(b.dx), Number(b.dy), Number(b.dz), idx);
    if (nz == null || (b.base_only && nz > EPS) || overlapsExisting(visualPallet, nx, ny, nz, Number(b.dx), Number(b.dy), Number(b.dz), idx)) return;
    setDraftPallet((prev) => {
      const next = clone(prev);
      next.boxes[idx] = { ...next.boxes[idx], x: Number(nx.toFixed(2)), y: Number(ny.toFixed(2)), z: Number(nz.toFixed(2)) };
      settleUnsupported(next, idx);
      return recompute(next);
    });
    setSelectedBoxIdx(idx);
    setDirty(true);
  };

  const removeSelected = () => {
    if (selectedBoxIdx == null || !selectedBox) return;
    updateDraft((p) => p.boxes.splice(selectedBoxIdx, 1));
    setSelectedBoxIdx(null);
  };

  const duplicateSelected = () => {
    if (!selectedBox) return;
    const spot = findFreePosition(visualPallet, Number(selectedBox.dx), Number(selectedBox.dy), Number(selectedBox.dz));
    if (!spot) return toast.error("Aynı kutu için boş alan bulunamadı.");
    updateDraft((p) => p.boxes.push({ ...clone(selectedBox), x: spot.x, y: spot.y, z: spot.z, locked: false }));
    toast.success(`${selectedBox.sku} kopyası palete eklendi.`);
  };

  const toggleSelectedLock = () => {
    if (selectedBoxIdx == null) return;
    updateDraft((p) => { p.boxes[selectedBoxIdx] = { ...p.boxes[selectedBoxIdx], locked: !p.boxes[selectedBoxIdx].locked }; }, selectedBoxIdx);
  };

  const addProducts = () => {
    const product = products[addSku];
    const qty = Number(addQty);
    if (!product || qty < 1) return toast.error("Ürün ve adet seçin.");
    const allowed = product.placement_rules?.allowed_rotations || [0, 1, 2, 3, 4, 5];
    const rotations = (addRotation === "auto" ? ROTATIONS : ROTATIONS.filter((r) => r.key === addRotation)).filter((r) => allowed.includes(Number(r.key)));
    if (!rotations.length) return toast.error("Seçilen rotasyon bu ürün için kullanılamıyor.");
    const working = clone(visualPallet);
    for (let count = 0; count < qty; count += 1) {
      let placement = null;
      for (const rot of rotations) {
        const [dx, dy, dz] = rot.fn(Number(product.length), Number(product.width), Number(product.height));
        const spot = findFreePosition(working, dx, dy, dz);
        if (spot && !(product.placement_rules?.base_only && spot.z > EPS)) {
          placement = { dx, dy, dz, spot };
          break;
        }
      }
      if (!placement) return toast.error(`${count} adet eklendi denemesinde durdu: kalan ürün için boş alan bulunamadı. Hiçbir değişiklik uygulanmadı.`);
      const productKg = Number(product.product_kg || 0);
      const packageKg = Number(product.package_kg || 0);
      working.boxes.push({
        sku: product.sku, inch: product.inch || product.attributes?.inch || "", gtip: product.gtip || "", description: product.description || "",
        x: placement.spot.x, y: placement.spot.y, z: placement.spot.z,
        dx: placement.dx, dy: placement.dy, dz: placement.dz,
        net_kg: Number(product.gross_kg || product.net_kg || productKg + packageKg), product_kg: productKg, package_kg: packageKg,
        locked: false, can_support_load: product.placement_rules?.can_support_load !== false,
        base_only: !!product.placement_rules?.base_only, placement_rules: product.placement_rules || {},
      });
    }
    setDraftPallet(recompute(working));
    setDirty(true);
    setSelectedBoxIdx(working.boxes.length - 1);
    toast.success(`${addSku} × ${qty} çalışma paletine eklendi. Kaydettiğinizde doğrulanacak.`);
  };

  const resetDraft = () => {
    if (!selected?.sourcePallet) return;
    setDraftPallet(recompute(clone(selected.sourcePallet)));
    const savedSupport = Number(selected.sourcePallet.manual_min_support);
    setSupportPercent(Number.isFinite(savedSupport) && savedSupport >= 0.4 && savedSupport <= 1 ? Math.round(savedSupport * 100) : 70);
    setSelectedBoxIdx(null);
    setDirty(false);
  };

  const saveGeometry = async () => {
    if (!selected || !visualPallet || readOnly) return;
    setSaving(true);
    try {
      await api.put(`/plans/${planId}/nested-pallet`, {
        action: "replace_geometry",
        pallet_no: selected.palletNo,
        boxes: visualPallet.boxes,
        min_support: Number(supportPercent) / 100,
      }, { headers: { "X-Plan-Lock-Token": lockToken || "" } });
      toast.success(`Palet ${selected.palletNo} manuel dizilimi %${supportPercent} destek eşiğiyle doğrulandı ve araç yeniden hesaplandı.`);
      setDirty(false);
      setEditing(null);
      setDraftPallet(null);
      setSelectedBoxIdx(null);
      await onUpdated?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const moveSelected = async () => {
    if (!selectedBox || !moveTarget) return toast.error("Hedef paleti seçin.");
    if (dirty) return toast.error("Önce mevcut manuel değişiklikleri kaydedin; sonra kutuyu başka palete taşıyın.");
    setSaving(true);
    try {
      await api.put(`/plans/${planId}/nested-pallet`, {
        action: "move", pallet_no: selected.palletNo, sku: selectedBox.sku, quantity: 1, target_pallet_no: Number(moveTarget),
      }, { headers: { "X-Plan-Lock-Token": lockToken || "" } });
      toast.success(`${selectedBox.sku} başka palete taşındı ve araç yeniden hesaplandı.`);
      setEditing(null);
      setDraftPallet(null);
      setSelectedBoxIdx(null);
      await onUpdated?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (!loads.length) return null;

  return <>
    <div className="bg-white border border-violet-200 px-3 py-3" data-testid="palletized-load-contents">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-violet-700 font-bold">Bu {unitLabel} İçindeki Paletler</div>
          <div className="text-[10px] text-slate-500 mt-0.5">3D Düzenle ile kutuları döndürün, X/Y/Z taşıyın, sürükleyin, çoğaltın veya yeni ürün ekleyin.</div>
        </div>
        <span className="text-[10px] font-mono border border-violet-200 bg-violet-50 text-violet-800 px-2 py-1">{loads.length} palet</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {loads.map((load) => <div key={load.palletNo} className="border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-start justify-between gap-2">
            <div><div className="font-display font-bold text-sm">Palet {load.palletNo}</div><div className="text-[10px] font-mono text-slate-500 mt-0.5">{load.boxCount} koli · {load.totalWeight.toFixed(1)} kg · {load.totalHeight.toFixed(1)} cm</div></div>
            <div className="flex items-center gap-1.5"><span className={`text-[9px] px-1.5 py-0.5 border font-semibold ${load.stackLevel > 1 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>{load.stackLevel > 1 ? "2. KAT" : "TABAN"}</span><button type="button" disabled={readOnly} onClick={() => openEditor(load.palletNo)} className="inline-flex items-center gap-1 border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 px-2 py-1 text-[9px] font-bold"><PencilSimple size={11} /> 3D Düzenle</button></div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">{load.skuCounts.map(([code, count]) => <span key={code} className="inline-flex items-center gap-1 bg-white border border-slate-300 px-2 py-1 text-[10px] font-mono"><b>{code}</b><span className="text-slate-400">×</span><span className="font-bold text-[#2563EB]">{count}</span></span>)}</div>
        </div>)}
      </div>
    </div>

    {selected && visualPallet && <div className="fixed inset-0 z-[220] bg-black/60 flex items-center justify-center p-3" onClick={() => !saving && closeEditor()}>
      <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-[1540px] h-[94vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <div><div className="font-display font-bold text-lg">Palet {selected.palletNo} · Tam Manuel 3D Düzenleme</div><div className="text-xs text-slate-500">{visualPallet.box_count} koli · {Number(visualPallet.total_weight || 0).toFixed(1)} kg · {Number(visualPallet.top_height || 0).toFixed(1)} cm · Minimum destek %{supportPercent}{dirty ? " · Kaydedilmemiş değişiklik var" : ""}</div></div>
          <button type="button" disabled={saving} onClick={closeEditor} className="p-2 text-slate-500 hover:text-slate-900"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] min-h-0 flex-1">
          <div className="bg-[#0F172A] min-h-[540px] relative border-r border-slate-300">
            <Pallet3D pallet={visualPallet} L={Number(visualPallet.L || 120)} W={Number(visualPallet.W || 100)} H={Number(visualPallet.H || Math.max(visualPallet.top_height, 200))} products={products} onSelectBox={(_, idx) => setSelectedBoxIdx(idx)} selectedIdx={selectedBoxIdx} multiSelIdxs={[]} hiddenLayerKeys={[]} onDropBox={onDropBox} />
          </div>

          <div className="bg-slate-50 p-4 overflow-y-auto space-y-4">
            {selectedBox ? <div className="bg-[#0F172A] text-white p-4 border border-slate-700">
              <div className="flex items-start justify-between"><div><div className="text-[10px] uppercase tracking-wider text-slate-400">Seçili Kutu #{selectedBoxIdx + 1}</div><div className="font-display text-xl font-bold mt-1">{selectedBox.sku}</div></div><button type="button" disabled={readOnly} onClick={toggleSelectedLock} className="border border-slate-600 p-2">{selectedBox.locked ? <LockKey size={16} /> : <LockOpen size={16} />}</button></div>
              <div className="text-[11px] font-mono text-slate-300 mt-2"><div>Konum: ({selectedBox.x}, {selectedBox.y}, {selectedBox.z}) cm</div><div>Boyut: {selectedBox.dx}×{selectedBox.dy}×{selectedBox.dz} cm</div></div>

              <div className="mt-4 text-[9px] uppercase tracking-wider text-slate-400">Döndür (90°)</div>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {["x", "y", "z"].map((axis) => <button key={axis} type="button" disabled={readOnly || selectedBox.locked} onClick={() => rotateSelected(axis)} className="border border-slate-600 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 py-2 text-[10px] font-bold inline-flex items-center justify-center gap-1"><ArrowsClockwise size={13} /> {axis.toUpperCase()} Ekseni</button>)}
              </div>

              <div className="mt-3 text-[9px] uppercase tracking-wider text-slate-400">Konum Kaydır · X/Y ±{STEP} cm · Z Yerçekimi/+{STEP}</div>
              <div className="grid grid-cols-6 gap-1 mt-1">
                <button disabled={readOnly || selectedBox.locked} onClick={() => nudgeSelected("x", -STEP)} className="border border-slate-600 py-2 text-[10px]">← X-</button>
                <button disabled={readOnly || selectedBox.locked} onClick={() => nudgeSelected("x", STEP)} className="border border-slate-600 py-2 text-[10px]">→ X+</button>
                <button disabled={readOnly || selectedBox.locked} onClick={() => nudgeSelected("y", -STEP)} className="border border-slate-600 py-2 text-[10px]">← Y-</button>
                <button disabled={readOnly || selectedBox.locked} onClick={() => nudgeSelected("y", STEP)} className="border border-slate-600 py-2 text-[10px]">→ Y+</button>
                <button disabled={readOnly || selectedBox.locked} onClick={() => nudgeSelected("z", -STEP)} className="border border-slate-600 py-2 text-[10px]">↓ Z-</button>
                <button disabled={readOnly || selectedBox.locked} onClick={() => nudgeSelected("z", STEP)} className="border border-slate-600 py-2 text-[10px]">↑ Z+</button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3"><button type="button" disabled={readOnly} onClick={duplicateSelected} className="bg-blue-600 hover:bg-blue-700 py-2 text-xs font-bold inline-flex items-center justify-center gap-1"><Copy size={14} /> Aynısını Çoğalt</button><button type="button" disabled={readOnly} onClick={removeSelected} className="bg-red-600 hover:bg-red-700 py-2 text-xs font-bold inline-flex items-center justify-center gap-1"><Trash size={14} /> Kutuyu Sil</button></div>

              <div className="mt-3 border-t border-slate-700 pt-3"><div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Başka Palete Taşı</div><div className="flex gap-2"><select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} className="flex-1 bg-slate-800 border border-slate-600 px-2 py-2 text-xs"><option value="">Hedef...</option>{loads.filter((row) => row.palletNo !== selected.palletNo).map((row) => <option key={row.palletNo} value={row.palletNo}>Palet {row.palletNo}</option>)}</select><button type="button" disabled={readOnly || saving || !moveTarget || dirty} onClick={moveSelected} className="bg-violet-600 disabled:opacity-40 px-3 text-xs font-bold inline-flex items-center gap-1"><ArrowsLeftRight size={13} /> Taşı</button></div>{dirty && <div className="text-[9px] text-amber-300 mt-1">Taşımadan önce manuel değişiklikleri kaydedin.</div>}</div>
            </div> : <div className="bg-white border border-slate-200 p-4 text-center text-xs text-slate-500">3D palet üzerinde bir kutu seçin. Üstteki SÜRÜKLE-BIRAK düğmesiyle XY manuel taşıma da yapabilirsiniz.</div>}

            <div className="bg-white border border-slate-200 p-4 space-y-3">
              <div className="font-bold text-sm">Palete Ürün Ekle</div>
              <select value={addSku} onChange={(e) => setAddSku(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white"><option value="">Ürün seçin...</option>{productOptions.map((p) => <option key={p.sku} value={p.sku}>{p.name ? `${p.name} · ` : ""}{p.sku}</option>)}</select>
              <div className="grid grid-cols-2 gap-2"><label><div className="text-[9px] uppercase text-slate-500 mb-1">Adet</div><input type="number" min="1" step="1" value={addQty} onChange={(e) => setAddQty(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm" /></label><label><div className="text-[9px] uppercase text-slate-500 mb-1">İlk Rotasyon</div><select value={addRotation} onChange={(e) => setAddRotation(e.target.value)} className="w-full border border-slate-300 px-2 py-2 text-sm"><option value="auto">Otomatik / Uyan ilk</option>{ROTATIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></label></div>
              <button type="button" disabled={readOnly || !addSku || Number(addQty) < 1} onClick={addProducts} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1"><Plus size={15} /> Çalışma Paletine Ekle</button>
              <div className="text-[10px] text-slate-500">Ürün önce boş bir noktaya eklenir. Ardından seçip rotasyon, X/Y/Z veya sürükle-bırak ile istediğiniz yere getirebilirsiniz.</div>
            </div>

            <div className="bg-white border border-slate-200 p-4 space-y-2" data-testid="nested-support-selector">
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Minimum Kutu Destek Oranı</div>
                <select value={supportPercent} onChange={(e) => { setSupportPercent(Number(e.target.value)); setDirty(true); }} disabled={readOnly || saving} className="w-full border border-slate-300 bg-white px-3 py-2.5 text-sm font-bold">
                  {SUPPORT_OPTIONS.map((value) => <option key={value} value={value}>%{value} minimum destek{value === 70 ? " · Önerilen" : ""}</option>)}
                </select>
              </label>
              <div className={`border px-3 py-2 text-[11px] leading-relaxed ${supportInfo.tone}`}>
                <div className="font-bold mb-0.5">{supportInfo.title}</div>
                <div>{supportInfo.text}</div>
              </div>
              <div className="text-[10px] text-slate-500">Destek oranı doluluk değildir. Üstteki tek bir kutunun tabanının ne kadarının alttaki kutuların üst yüzeyine gerçekten bastığını ifade eder.</div>
            </div>

            <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">Kaydederken çakışma, sınır, ürün rotasyonu, base-only ve seçtiğiniz destek oranı backend tarafından tekrar doğrulanır. Manuel koordinatlar başarılıysa korunur; yalnız dış araç yerleşimi yeniden optimize edilir.</div>

            <div className="grid grid-cols-2 gap-2 sticky bottom-0 bg-slate-50 pt-2"><button type="button" disabled={saving || !dirty} onClick={resetDraft} className="border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold disabled:opacity-40">Değişiklikleri Sıfırla</button><button type="button" disabled={saving || readOnly || !dirty} onClick={saveGeometry} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-3 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1"><FloppyDisk size={15} /> {saving ? "Doğrulanıyor..." : "Kaydet & Aracı Hesapla"}</button></div>
          </div>
        </div>
      </div>
    </div>}
  </>;
}
